// Central company facts — real contact details, centralized for easy updates.
// Vancouver & Spokane operate as service areas (mobile crews, no walk-in office).

export const COMPANY = {
  name: "IDB Plumbing Solutions",
  legalLine: "Licensed, Bonded & Insured — License #IDBPLPS753P8",
  license: "License #IDBPLPS753P8",
  phone: "(425) 243-4006",
  // Structured-data (JSON-LD) phone in the NAP-kit format. Must stay identical
  // across every citation profile listed in `sameAs` below.
  phoneSchema: "+1-425-243-4006",
  phoneHref: "tel:+14252434006",
  smsHref: "sms:+14252434006",
  email: "Sebastian@idb-plumbing.com",
  tagline:
    "A Washington-based plumbing company with statewide reach and local crews — big enough to handle anything, local enough to show up fast.",
  brandStatement:
    "Washington plumbers who do the job right, explain it plainly, and leave your home cleaner than we found it.",
  siteUrl: "https://idb-plumbing.com",
  // Public profile URLs for LocalBusiness schema `sameAs` (local SEO). The first
  // three mirror SOCIAL_LINKS below; the rest are directory/citation profiles.
  // Add the Google Business Profile / Yelp URLs here as they go live.
  sameAs: [
    "https://www.facebook.com/idbplumbingsolutions",
    "https://www.instagram.com/idbplumbing",
    "https://www.linkedin.com/company/idbplumbing",
    "https://nextdoor.com/page/idb-plumbing-solutions-federal-way-wa/",
    "https://foursquare.com/v/idb-plumbing-solutions/6aa5edb973953b0a60e43552",
    "https://www.houzz.com/pro/sebastian-suciu56",
    "https://www.buildzoom.com/contractor/idb-plumbing-solutions",
    "https://www.alignable.com/federal-way-wa/idb-plumbing-solutions",
    "https://www.hotfrog.com/company/e0959475c7253da660c0522d3ddb9f4d",
  ],
};

// Social profiles shown in the footer (icon names map to lucide-react icons in
// Layout.jsx). Order = display order. No YouTube channel by owner decision.
export const SOCIAL_LINKS = [
  { id: "facebook", label: "Facebook", href: "https://www.facebook.com/idbplumbingsolutions", handle: "@idbplumbingsolutions" },
  { id: "instagram", label: "Instagram", href: "https://www.instagram.com/idbplumbing", handle: "@idbplumbing" },
  { id: "linkedin", label: "LinkedIn", href: "https://www.linkedin.com/company/idbplumbing", handle: "idbplumbing" },
  // SEO audit batch 2, Part 6. A Google icon is added later, once the Google Business Profile is verified.
  { id: "yelp", label: "Yelp", href: "https://www.yelp.com/biz/idb-plumbing-solutions-federal-way-3", handle: "idb-plumbing-solutions-federal-way-3" },
];

// Sitewide promotional offer shown in the dismissible banner.
// Bump `id` to re-show the banner to visitors who dismissed a previous offer.
export const PROMO = {
  id: "water-heater-300-v1",
  headline: "$300 off Water Heater Installation",
  detail: "Mention this offer when scheduling.",
  ends: "September 30, 2026",
  endsISO: "2026-09-30", // banner auto-hides after this day; dismissal is remembered until then
  href: "/services/water-heaters",
};

// Sister companies (shared ownership) — used for cross-linking and interim
// trust signals while this brand's own review profile grows.
export const FAMILY_BRANDS = [
  { name: "ID Builder Construction", href: "https://idbuilderconstruction.com", note: "General construction · established track record" },
  { name: "IDB HVAC", href: "https://idbhvac.com", note: "Heating & cooling" },
];

export const OFFICES = [
  {
    slug: "federal-way",
    city: "Federal Way",
    state: "WA",
    label: "Federal Way (Headquarters)",
    shortLabel: "Federal Way HQ",
    isHQ: true,
    isServiceArea: false,
    address: "36058 21st Ln S, Federal Way, WA 98003",
    streetAddress: "36058 21st Ln S",
    postalCode: "98003",
    phone: "(425) 243-4006",
    phoneHref: "tel:+14252434006",
    hours: [
      { days: "Mon–Fri", time: "7:00 AM – 6:00 PM" },
      { days: "Saturday", time: "8:00 AM – 4:00 PM" },
      { days: "Emergencies", time: "24/7" },
    ],
    radius: "Serving the greater Seattle–Tacoma metro and South King County",
    geo: { lat: 47.3223, lng: -122.3126 },
  },
  {
    slug: "vancouver",
    city: "Vancouver",
    state: "WA",
    label: "Vancouver area",
    shortLabel: "Vancouver",
    isHQ: false,
    isServiceArea: true,
    // Service area, not an office: no street address and no posted hours.
    address: "Serving Vancouver & Clark County",
    phone: "(425) 243-4006",
    phoneHref: "tel:+14252434006",
    hours: [],
    radius: "Serving Southwest Washington and Clark County",
    geo: { lat: 45.6387, lng: -122.6615 },
  },
  {
    slug: "spokane",
    city: "Spokane",
    state: "WA",
    label: "Spokane area",
    shortLabel: "Spokane",
    isHQ: false,
    isServiceArea: true,
    // Service area, not an office: no street address and no posted hours.
    address: "Serving Greater Spokane",
    phone: "(425) 243-4006",
    phoneHref: "tel:+14252434006",
    hours: [],
    radius: "Serving Eastern Washington and the Inland Northwest",
    geo: { lat: 47.6588, lng: -117.426 },
  },
];

export const IMAGES = {
  // AI-rendered brand photography — real IDB logo applied to vehicles.
  // Files live in /public/images/brand/ (regenerate via /app/scripts/generate_brand_images.py).
  // Root-relative paths work on any domain; SEO.jsx absolutizes them for og:image.
  brandHeroVanDusk: "/images/brand/hero-van-dusk.jpg",
  brandVanDriveway: "/images/brand/van-driveway.jpg",
  brandTechAtVan: "/images/brand/tech-at-van.jpg",
  brandTruckJobsite: "/images/brand/truck-jobsite.jpg",
  brandVanFleet: "/images/brand/van-fleet.jpg",
  brandEmergencyNight: "/images/brand/emergency-night.jpg",
  brandRepipeInterior: "/images/brand/repipe-interior.jpg",
  brandDrainService: "/images/brand/drain-service.jpg",
  brandWaterHeaterInstall: "/images/brand/water-heater-install.jpg",
  brandGasLineWork: "/images/brand/gas-line-work.jpg",
  brandFixtureInstall: "/images/brand/fixture-install.jpg",
  brandCommercialSite: "/images/brand/commercial-site.jpg",
  heroHome:
    "https://images.pexels.com/photos/36842619/pexels-photo-36842619.jpeg?auto=compress&cs=tinysrgb&w=1920",
  fixtureDetail:
    "https://images.unsplash.com/photo-1568564485646-8387cc48ed0e?auto=format&fit=crop&w=1600&q=80",
  faucetMinimal:
    "https://images.unsplash.com/photo-1544616587-d2ce187fa195?auto=format&fit=crop&w=1600&q=80",
  copperPipes:
    "https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=1600&q=80",
  plumberSink:
    "https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?auto=format&fit=crop&w=1600&q=80",
  bathroomFaucet:
    "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1600&q=80",
  kitchen:
    "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1600&q=80",
  industrialPipes:
    "https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1600&q=80",
  construction:
    "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1600&q=80",
  engineerPipes:
    "https://images.unsplash.com/photo-1581094794329-c8112a89af12?auto=format&fit=crop&w=1600&q=80",
  tools:
    "https://images.unsplash.com/photo-1521207418485-99c705420785?auto=format&fit=crop&w=1600&q=80",
  team:
    "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1600&q=80",
  homeExterior:
    "https://images.unsplash.com/photo-1580901368919-7738efb0f87e?auto=format&fit=crop&w=1600&q=80",
  bathroomModern:
    "https://images.unsplash.com/photo-1564540583246-934409427776?auto=format&fit=crop&w=1600&q=80",
  shower:
    "https://images.unsplash.com/photo-1604709177225-055f99402ea3?auto=format&fit=crop&w=1600&q=80",
  faucetChrome:
    "https://images.unsplash.com/photo-1585128792020-803d29415281?auto=format&fit=crop&w=1600&q=80",
};
